const POPUP_MEDIA_MAX_WAIT = 2000;

class OrihaneEmailPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!(this.dialog instanceof HTMLDialogElement)) return;

    this.closeControls = this.querySelectorAll('[data-popup-dismiss]');
    this.copyButton = this.querySelector('[data-popup-copy]');
    this.forceOpen = Boolean(this.querySelector('[data-popup-success], .orihane-email-popup__message--error'));
    this.enabled = this.dataset.enabled === 'true';
    this.dismissedByControl = false;
    this.isOpening = false;
    this.storageKey = `orihane-email-popup:${this.id}`;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.closeControls.forEach((control) => {
      control.addEventListener('click', () => this.dismiss(), { signal });
    });
    this.dialog.addEventListener('cancel', () => this.rememberDismissal(), { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dismiss();
    }, { signal });
    this.copyButton?.addEventListener('click', () => this.copyCode(), { signal });

    if (this.forceOpen) {
      this.rememberSubscriber();
      this.open();
      return;
    }

    if (!this.enabled || this.wasSuppressed()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0);
    this.openTimer = window.setTimeout(() => this.open(), delay);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.copyTimer);
  }

  async open() {
    if (!this.isConnected || !this.dialog || this.dialog.open || this.isOpening) return;

    this.isOpening = true;

    try {
      await this.loadPopupMedia();
    } catch {
      // Media is non-essential. Continue to the subscription form if preparation fails.
    }

    try {
      if (
        !this.isConnected
        || !this.dialog
        || this.dialog.open
        || (!this.forceOpen && (!this.enabled || this.wasSuppressed()))
      ) return;

      this.dialog.showModal();
      this.querySelector('.orihane-email-popup__close')?.focus();
    } catch {
      // A browser-level dialog failure should not block the page.
    } finally {
      this.isOpening = false;
    }
  }

  loadPopupMedia() {
    if (this.popupMediaPromise) return this.popupMediaPromise;

    const sources = this.querySelectorAll('source[data-popup-srcset]');
    const images = this.querySelectorAll('img[data-popup-image]');

    sources.forEach((source) => {
      source.srcset = source.dataset.popupSrcset || '';
      source.removeAttribute('data-popup-srcset');
    });

    images.forEach((image) => {
      image.sizes = image.dataset.popupSizes || '';
      image.srcset = image.dataset.popupSrcset || '';
      image.src = image.dataset.popupSrc || '';
      image.removeAttribute('data-popup-sizes');
      image.removeAttribute('data-popup-srcset');
      image.removeAttribute('data-popup-src');
    });

    this.popupMediaPromise = Promise.all([...images].map((image) => this.waitForImage(image))).then((results) => {
      if (!this.isConnected) return;

      results.forEach(({ image, status }) => {
        if (status === 'error' || status === 'timeout') image.hidden = true;
      });
    });
    return this.popupMediaPromise;
  }

  waitForImage(image) {
    return new Promise((resolve) => {
      let finished = false;
      let timeout;
      const signal = this.abortController?.signal;

      const cleanup = () => {
        window.clearTimeout(timeout);
        image.removeEventListener('load', handleLoad);
        image.removeEventListener('error', handleError);
        signal?.removeEventListener('abort', handleAbort);
      };

      const finish = (status) => {
        if (finished) return;
        finished = true;
        cleanup();
        resolve({ image, status });
      };

      const handleLoad = () => {
        if (typeof image.decode !== 'function') {
          finish('loaded');
          return;
        }

        try {
          const decoded = image.decode();
          if (!decoded || typeof decoded.then !== 'function') {
            finish('loaded');
            return;
          }

          decoded.then(
            () => finish('loaded'),
            () => finish('decode-error'),
          );
        } catch {
          finish('decode-error');
        }
      };
      const handleError = () => finish('error');
      const handleAbort = () => finish('aborted');

      image.addEventListener('load', handleLoad, { once: true });
      image.addEventListener('error', handleError, { once: true });
      signal?.addEventListener('abort', handleAbort, { once: true });
      timeout = window.setTimeout(() => finish('timeout'), POPUP_MEDIA_MAX_WAIT);

      if (signal?.aborted) {
        handleAbort();
        return;
      }

      if (image.complete) {
        if (image.naturalWidth > 0) {
          handleLoad();
        } else {
          handleError();
        }
      }
    });
  }

  dismiss() {
    this.dismissedByControl = true;
    this.rememberDismissal();
    this.dialog?.close();
  }

  wasSuppressed() {
    try {
      const entry = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
      return Boolean(entry?.until && entry.until > Date.now());
    } catch {
      return false;
    }
  }

  rememberDismissal() {
    this.saveSuppression(Number(this.dataset.dismissDays) || 7);
  }

  rememberSubscriber() {
    this.saveSuppression(Number(this.dataset.subscriberDays) || 365);
  }

  saveSuppression(days) {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify({ until: Date.now() + days * 86400000 }));
    } catch {
      // The popup remains functional when storage is unavailable.
    }
  }

  async copyCode() {
    if (!(this.copyButton instanceof HTMLButtonElement)) return;
    const code = this.copyButton.dataset.copyText;
    if (!code) return;

    const copied = await this.copyText(code);
    if (!copied) return;

    const originalLabel = this.copyButton.dataset.copyLabel || this.copyButton.textContent;
    this.copyButton.textContent = this.copyButton.dataset.copiedLabel || 'Copied!';
    window.clearTimeout(this.copyTimer);
    this.copyTimer = window.setTimeout(() => {
      this.copyButton.textContent = originalLabel;
    }, 1800);
  }

  async copyText(value) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      const fallback = document.createElement('textarea');
      fallback.value = value;
      fallback.setAttribute('readonly', '');
      fallback.style.position = 'fixed';
      fallback.style.opacity = '0';
      document.body.append(fallback);
      fallback.select();
      const copied = document.execCommand('copy');
      fallback.remove();
      return copied;
    }
  }
}

if (!customElements.get('orihane-email-popup')) {
  customElements.define('orihane-email-popup', OrihaneEmailPopup);
}
